/**
 * Attention-event detection (pipeline stage 8).
 *
 * An *event* is a completed episode: orientation left the workstation-facing cone at one
 * point in time and came back at another. Recording episodes rather than a per-second stream
 * is both the privacy-minimising choice (§28) and the reviewable one — "four attention-away
 * events totalling 90 seconds" can be discussed with a person; 3,600 per-second rows cannot.
 *
 * Debouncing is the substance of this module. Without it every head movement past a boundary
 * would produce an event, the count would be dominated by noise, and the statistics would be
 * worse than having none. The §11 bands are applied to *completed* runs, so a two-second
 * glance is discarded rather than recorded and later explained away.
 */

import type { EventPolicy } from '../config.js';
import {
  createEvent,
  type AttentionEvent,
  type Direction,
  type DirectionSample,
  type EventSeverity,
} from '../models.js';

/** Map a completed run duration onto a §11 severity band. */
export function classifySeverity(durationSeconds: number, policy: EventPolicy): EventSeverity {
  if (durationSeconds < policy.ignoreBelowSeconds) return 'ignored';
  if (durationSeconds < policy.attentionAwayMinSeconds) return 'minor_shift';
  if (durationSeconds < policy.potentiallySignificantSeconds) return 'attention_away';
  return 'potentially_significant';
}

interface Run {
  startMonotonic: number;
  startUtc: Date;
  lastMonotonic: number;
  lastUtc: Date;
  directions: Map<Direction, number>;
  confidences: number[];
  reasons: string[];
}

function newRun(sample: DirectionSample): Run {
  return {
    startMonotonic: sample.monotonicTs,
    startUtc: sample.timestampUtc,
    lastMonotonic: sample.monotonicTs,
    lastUtc: sample.timestampUtc,
    directions: new Map(),
    confidences: [],
    reasons: [],
  };
}

function extendRun(run: Run, sample: DirectionSample): void {
  run.lastMonotonic = sample.monotonicTs;
  run.lastUtc = sample.timestampUtc;
  run.directions.set(sample.direction, (run.directions.get(sample.direction) ?? 0) + 1);
  run.confidences.push(sample.confidence);
  for (const reason of sample.reasons) {
    if (!run.reasons.includes(reason)) run.reasons.push(reason);
  }
}

function runDuration(run: Run): number {
  return Math.max(0, run.lastMonotonic - run.startMonotonic);
}

function dominantDirection(run: Run): Direction {
  let best: Direction = 'not_detected';
  let bestCount = -1;
  for (const [direction, count] of run.directions) {
    if (count > bestCount) {
      bestCount = count;
      best = direction;
    }
  }
  return best;
}

function meanConfidence(run: Run): number {
  if (run.confidences.length === 0) return 0;
  return run.confidences.reduce((total, value) => total + value, 0) / run.confidences.length;
}

function sampleCount(run: Run): number {
  let total = 0;
  for (const count of run.directions.values()) total += count;
  return total;
}

export interface EventDetectorOptions {
  sessionId?: string;
  agentVersion?: string;
  modelVersion?: string;
  configVersion?: string;
}

/**
 * Detect attention-away and face-lost episodes from a direction sample stream.
 *
 * A run's end is measured at the *last sample that was still away*, not at the first sample
 * that came back. At 10 fps that understates each duration by up to 100 ms, which is the
 * right direction for the error to point: durations that appear in a record about a person
 * should never be rounded upward.
 */
export class AttentionEventDetector {
  private readonly policy: EventPolicy;
  private sessionId: string;
  private agentVersion: string;
  private modelVersion: string;
  private configVersion: string;

  private awayRun: Run | null = null;
  private lostRun: Run | null = null;
  private emittedAt: number[] = [];
  private suppressedSince: number | null = null;
  private suppressedCount = 0;
  private minorShifts = 0;
  private ignored = 0;

  constructor(policy: EventPolicy, options: EventDetectorOptions = {}) {
    this.policy = policy;
    this.sessionId = options.sessionId ?? '';
    this.agentVersion = options.agentVersion ?? '';
    this.modelVersion = options.modelVersion ?? '';
    this.configVersion = options.configVersion ?? '';
  }

  /** The model version is only known once a provider has loaded. */
  setModelVersion(modelVersion: string): void {
    this.modelVersion = modelVersion;
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  /** The resolved policy fingerprint, stamped on every event for traceability. */
  setConfigVersion(configVersion: string): void {
    this.configVersion = configVersion;
  }

  get stampedConfigVersion(): string {
    return this.configVersion;
  }

  /** Sub-threshold shifts observed. Counted for §11 statistics, not emitted. */
  get minorShiftCount(): number {
    return this.minorShifts;
  }

  get ignoredCount(): number {
    return this.ignored;
  }

  get suppressedEventCount(): number {
    return this.suppressedCount;
  }

  get openRunDuration(): number | null {
    return this.awayRun === null ? null : runDuration(this.awayRun);
  }

  observe(sample: DirectionSample): AttentionEvent[] {
    const events: AttentionEvent[] = [];

    if (sample.direction === 'not_detected') {
      // A face disappearing closes any open attention-away run: we can no longer observe
      // the orientation, so we must stop claiming to measure it.
      if (this.awayRun !== null) events.push(...this.closeAwayRun(true));

      const faceAbsent =
        sample.quality === null ||
        sample.quality.flags.includes('no_face') ||
        sample.quality.flags.includes('camera_unavailable');

      if (!faceAbsent) {
        // A visible face whose pose could not be solved is a measurement failure, not an
        // absence. Recording it as one would put a gap in someone's day for a solver hiccup.
        if (this.lostRun !== null) events.push(...this.closeLostRun(false));
        return events;
      }

      if (this.lostRun === null) this.lostRun = newRun(sample);
      extendRun(this.lostRun, sample);
      return events;
    }

    if (this.lostRun !== null) events.push(...this.closeLostRun(false));

    if (sample.direction === 'forward') {
      if (this.awayRun !== null) events.push(...this.closeAwayRun(false));
      return events;
    }

    if (this.awayRun === null) this.awayRun = newRun(sample);
    extendRun(this.awayRun, sample);
    return events;
  }

  /**
   * Close any open runs, e.g. at session end.
   *
   * Open runs are emitted with `truncated: true` metadata. Silently dropping an in-progress
   * episode would systematically under-report exactly the longest ones.
   */
  flush(): AttentionEvent[] {
    const events: AttentionEvent[] = [];
    if (this.awayRun !== null) events.push(...this.closeAwayRun(true));
    if (this.lostRun !== null) events.push(...this.closeLostRun(true));
    return events;
  }

  private closeAwayRun(truncated: boolean): AttentionEvent[] {
    const run = this.awayRun;
    this.awayRun = null;
    if (run === null) return [];

    const duration = runDuration(run);
    const severity = classifySeverity(duration, this.policy);

    if (severity === 'ignored') {
      this.ignored += 1;
      return [];
    }
    if (severity === 'minor_shift') {
      this.minorShifts += 1;
      if (!this.policy.emitMinorShifts) return [];
    }

    const direction = dominantDirection(run);
    const evidence = [
      `orientation left the workstation-facing cone for ${duration.toFixed(1)}s`,
      `dominant orientation: ${direction}`,
    ];
    if (run.directions.size > 1) {
      const spread = [...run.directions.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => `${key}x${count}`)
        .join(', ');
      evidence.push(`orientation samples in this episode: ${spread}`);
    }
    evidence.push(...run.reasons.slice(0, 4));
    if (truncated) {
      evidence.push(
        'episode was still in progress when measurement stopped; duration is a lower bound',
      );
    }

    return this.emit(
      createEvent({
        eventType: 'attention_away',
        sessionId: this.sessionId,
        startTimeUtc: run.startUtc,
        endTimeUtc: run.lastUtc,
        durationSeconds: duration,
        direction,
        severity,
        confidence: meanConfidence(run),
        evidence,
        agentVersion: this.agentVersion,
        modelVersion: this.modelVersion,
        configVersion: this.configVersion,
        metadata: {
          sample_count: sampleCount(run),
          truncated,
          direction_samples: Object.fromEntries(run.directions),
        },
      }),
      run.lastMonotonic,
    );
  }

  private closeLostRun(truncated: boolean): AttentionEvent[] {
    const run = this.lostRun;
    this.lostRun = null;
    if (run === null) return [];

    const duration = runDuration(run);
    if (duration < this.policy.faceLostAwaySeconds) {
      // A brief loss of detection is a sensor artefact, not an absence. Recording it would
      // turn a hand raised to the face into a gap in someone's day.
      return [];
    }

    const evidence = [
      `no face detected for ${duration.toFixed(1)}s`,
      `absence threshold is ${this.policy.faceLostAwaySeconds.toFixed(0)}s`,
      'an absent face means orientation could not be measured; it is not itself an ' +
        'indication of whether work occurred',
      ...run.reasons.slice(0, 3),
    ];
    if (truncated) evidence.push('period was still in progress when measurement stopped');

    return this.emit(
      createEvent({
        eventType: 'face_lost',
        sessionId: this.sessionId,
        startTimeUtc: run.startUtc,
        endTimeUtc: run.lastUtc,
        durationSeconds: duration,
        direction: 'not_detected',
        severity: classifySeverity(duration, this.policy),
        confidence: absenceConfidence(duration, this.policy),
        evidence,
        agentVersion: this.agentVersion,
        modelVersion: this.modelVersion,
        configVersion: this.configVersion,
        metadata: { sample_count: sampleCount(run), truncated },
      }),
      run.lastMonotonic,
    );
  }

  /**
   * Apply the per-minute rate cap.
   *
   * Hitting the cap means something is wrong with the sensor or the thresholds, not that the
   * employee moved thirty times in a minute. Suppression is therefore reported as a
   * `quality_degraded` event rather than silently dropping data.
   */
  private emit(event: AttentionEvent, now: number): AttentionEvent[] {
    const cutoff = now - 60;
    this.emittedAt = this.emittedAt.filter((timestamp) => timestamp >= cutoff);

    if (this.emittedAt.length >= this.policy.maxEventsPerMinute) {
      if (this.suppressedSince === null) this.suppressedSince = now;
      this.suppressedCount += 1;
      return [];
    }

    const emitted: AttentionEvent[] = [];
    if (this.suppressedSince !== null) {
      emitted.push(
        createEvent({
          eventType: 'quality_degraded',
          sessionId: this.sessionId,
          startTimeUtc: event.startTimeUtc,
          endTimeUtc: event.endTimeUtc,
          durationSeconds: Math.max(0, now - this.suppressedSince),
          direction: 'not_detected',
          severity: 'attention_away',
          confidence: 1,
          evidence: [
            `${this.suppressedCount} attention events were suppressed by the ` +
              `${this.policy.maxEventsPerMinute}/minute rate cap`,
            'a sustained burst of events usually indicates camera instability or thresholds ' +
              'set too tightly, and should be reviewed before the surrounding metrics are ' +
              'relied upon',
          ],
          agentVersion: this.agentVersion,
          modelVersion: this.modelVersion,
          configVersion: this.configVersion,
          metadata: { suppressed_event_count: this.suppressedCount },
        }),
      );
      this.suppressedSince = null;
      this.suppressedCount = 0;
    }

    this.emittedAt.push(now);
    emitted.push(event);
    return emitted;
  }
}

/** Longer absences are more certainly absences rather than detection failures. */
function absenceConfidence(duration: number, policy: EventPolicy): number {
  const span = Math.max(policy.faceLostAwaySeconds * 3, policy.faceLostAwaySeconds + 1e-6);
  const fraction = Math.min(1, Math.max(0, (duration - policy.faceLostAwaySeconds) / span));
  return 0.55 + 0.4 * fraction;
}
